import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';
import tw, { styled } from 'twin.macro';

import { ButtonV2, showToast, Spinner } from '@restworld/ui-ds';

import { getServerTranslations } from '../../get-server-side-translations';
import { EmployerSecondaryLayout } from '../../layouts';
import { EmployerPageContentWrapper } from '../../components/employer-page-content-wrapper';
import { ShowPaymentMethods } from '../../components/job-position-onboarding/steps/show-payment-methods';
import { updatedDefaultPaymentMethodAtom } from '../../components/job-position-onboarding/use-job-positions-onboarding';
import { usePaymentCompanyData } from '../../components/job-position-onboarding/hooks/use-payment-company-data';

const PageWrapper = styled(EmployerPageContentWrapper)`
  ${tw`py-[63px] md:py-[80px]`}
`;

const FooterWrapper = styled.div`
  ${tw`flex justify-center gap-4 w-full mt-4`}
`;

const ManagePaymentMethodsPage: React.FC = () => {
  const { t } = useTranslation('payment-and-invoices');
  const router = useRouter();
  const updatedDefaultPaymentMethod = useAtomValue(updatedDefaultPaymentMethodAtom);
  const setUpdatedDefaultPaymentMethod = useSetAtom(updatedDefaultPaymentMethodAtom);
  const { updateDefaultPaymentMethod, isUpdatingDefault } = usePaymentCompanyData();

  const handleNavigateToAddNew = React.useCallback(() => {
    router.push('/employer/add-payment-method');
  }, [router]);

  const handleConfirm = React.useCallback(async () => {
    if (updatedDefaultPaymentMethod) {
      try {
        await updateDefaultPaymentMethod({
          stripeCustomerId: updatedDefaultPaymentMethod.customerId,
          paymentMethodId: updatedDefaultPaymentMethod.paymentMethodId,
        });

        showToast(t('payment_method_updated_successfully'), 'success', {
          position: 'top-right',
          autoClose: 3000,
        });

        // Reset atom
        setUpdatedDefaultPaymentMethod(null);
      } catch (error) {
        showToast(t('payment_method_update_error'), 'error', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }
    }

    router.push('/employer/payments-and-invoices');
  }, [updatedDefaultPaymentMethod, updateDefaultPaymentMethod, setUpdatedDefaultPaymentMethod, router, t]);

  const renderFooter = React.useCallback(() => {
    return (
      <FooterWrapper>
        <ButtonV2 variant="primary" onClick={handleConfirm} disabled={isUpdatingDefault}>
          {isUpdatingDefault ? <Spinner color="secondary" size="sm" /> : t('confirm', { defaultValue: 'Confirm' })}
        </ButtonV2>
      </FooterWrapper>
    );
  }, [handleConfirm, isUpdatingDefault, t]);

  return (
    <EmployerSecondaryLayout title={t('manage_payment_methods')}>
      <PageWrapper>
        <ShowPaymentMethods onNavigateToAddNew={handleNavigateToAddNew} renderFooter={renderFooter} />
      </PageWrapper>
    </EmployerSecondaryLayout>
  );
};

export default ManagePaymentMethodsPage;

export const getStaticProps = async ({ locale }) => {
  const transConfig = await getServerTranslations(locale, ['common', 'payment-and-invoices', 'jp-onboarding']);
  return {
    props: {
      ...transConfig,
    },
  };
};
